import React, { useState, useContext, useEffect, useCallback } from 'react';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { NavigateToWindowWithNativeEvents } from '@components/_common/link/link';

import {
  PreOrderModal,
  CancellationForm,
  BodyHeader,
  BodyText,
  ConfirmationText,
  CancellationSuccess,
  IconContainer,
  CancellationFailure,
  CustomForm,
  CustomTextArea,
  ButtonContainer,
  CustomPrimaryButton,
  CustomTertiaryButton,
} from './CancelPreOrder.styles';
import DuoCheckIcon from './DuoCheckIcon';
import { DefaultSpinner } from '../spinner/Spinner';

import { AnalyticEvents, AppRoute, CancellationReason } from '../../../constants';

const CancelPreOrderModal = ({ isOpen, closeModal, vehicleDetails, submissionHandler }: any) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const [isCancellationSuccess, setCancellationSuccess] = useState(false);
  const [isCancellationFailure, setCancellationFailure] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { vehicleId, vehicleState } = vehicleDetails; // TODO: insert vehicle id and verify the inventory type

  const validationSchema = Yup.object().shape({
    cancellationText: Yup.string(),
  });

  const eventPayload = {
    vehicleId,
    vehicleState,
  };

  // Modal Window close handler
  const handleCloseModal = () => {
    closeModal();
    setCancellationSuccess(false);
    setCancellationFailure(false);
    setIsLoading(false);

    if (!isCancellationSuccess) {
      trackEvent(AnalyticEvents.cancelOrderDismissed, eventPayload);
      datadogRum.addAction(AnalyticEvents.cancelOrderDismissed, eventPayload);
    } else {
      NavigateToWindowWithNativeEvents({
        url: AppRoute.MY_CARVANA,
        event: 'continue_to_my_cars',
      });
    }
  };

  const keyboardHandler = useCallback(
    (event: any) => {
      // event.keyCode === 27  is the "Esc" key
      if (event.keyCode === 27 && isOpen) {
        handleCloseModal();
      }
    },
    [isOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', keyboardHandler);

    return () => {
      document.removeEventListener('keydown', keyboardHandler);
    };
  }, [keyboardHandler]);

  // Form Submit
  const handleFormSubmit = (values: any, actions: any) => {
    setIsLoading(true);
    const { cancellationText } = values;

    // Event on cancellation initiation
    trackEvent(AnalyticEvents.cancelOrderConfirmed, eventPayload);
    datadogRum.addAction(AnalyticEvents.cancelOrderConfirmed, eventPayload);

    const cancellationPayload = {
      vehicleId,
      notes: cancellationText,
      reasons: [CancellationReason.CUSTOMER_SELF_CANCELLED],
    };

    // TODO: Call Pre-order Cancellation API
    const env = process.env.NODE_ENV;

    submissionHandler(cancellationPayload, env)
      .then(() => {
        setCancellationSuccess(true);

        // Event on successful cancellation
        trackEvent(AnalyticEvents.orderCancelled, eventPayload);
        datadogRum.addAction(AnalyticEvents.orderCancelled, eventPayload);
      })
      .catch((error: any) => {
        console.error(error);
        setCancellationFailure(true);
      })
      .finally(() => {
        setIsLoading(false);
        actions.setSubmitting(false);
      });
  };

  /** Close and redirect user to /cars on closing the modal
   * after successful cancellation
   * */
  const handleCancelSuccessClose = () => {
    closeModal();
    NavigateToWindowWithNativeEvents({
      url: AppRoute.CARS,
      event: 'continue_to_srp',
    });
  };

  const placeholder = 'Why did you choose to cancel?';

  // Cancellation Text Area
  const CancellationTextArea = (props: any) => {
    const {
      value: { cancellationText },
      onChange,
    } = props;
    return (
      <CustomTextArea
        type="text"
        id="cancellationText"
        placeholder={placeholder}
        onChange={onChange}
        value={cancellationText}
        data-testid="cancellation-text-area"
      />
    );
  };

  const showCancellationForm = !isLoading && !isCancellationSuccess && !isCancellationFailure;
  const showCancellationFailure = !isCancellationSuccess && isCancellationFailure;

  const initialValues = {
    cancellationText: '',
  };

  const loaderStyles = {
    minHeight: '338px',
    marginTop: '20%',
  };

  return (
    <PreOrderModal
      data-testid="cancel-preorder-header-text"
      headerText="Cancel Pre-Order"
      closeModal={handleCloseModal}
      isOpen={isOpen}
    >
      {isLoading && <DefaultSpinner customStyles={loaderStyles} />}
      {/* Pre-order Cancellation Form */}
      {showCancellationForm && (
        <CancellationForm>
          <BodyHeader data-testid="cancel-preorder-body-header-text">Cancellation confirmation</BodyHeader>
          <BodyText data-testid="cancel-preorder-body-text">
            If you choose to cancel we will refund the credit card you used to pre-order this vehicle. You should see a
            credit appear within 7 days.
          </BodyText>
          <ConfirmationText data-testid="cancel-preorder-confirmation-text">
            Are you sure you want to cancel?
          </ConfirmationText>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
            <CustomForm>
              <Field as={CancellationTextArea} />
              <ButtonContainer>
                <CustomPrimaryButton data-testid="cancel-preorder-confirm-button" type="submit">
                  YES, CANCEL My PRE-ORDER
                </CustomPrimaryButton>
                <CustomTertiaryButton data-testid="cancel-preorder-cancel-button" onClick={handleCloseModal}>
                  NO THANKS, I&apos;LL KEEP MY PRE-ORDER
                </CustomTertiaryButton>
              </ButtonContainer>
            </CustomForm>
          </Formik>
        </CancellationForm>
      )}
      {/* Pre-order Cancellation Success */}
      {isCancellationSuccess && (
        <CancellationSuccess>
          <IconContainer>
            <DuoCheckIcon />
          </IconContainer>
          <BodyHeader data-testid="cancel-preorder-completed-header-text">Pre-order reservation cancelled</BodyHeader>
          <BodyText data-testid="cancel-preorder-completed-body-text">Thank you for your feedback.</BodyText>
          <BodyText data-testid="cancel-preorder-completed-cta-text">
            Looking for a different vehicle? We add new inventory every day.
          </BodyText>
          <CustomPrimaryButton
            data-testid="cancel-preorder-completed-search-inventory-button"
            onClick={handleCancelSuccessClose}
          >
            Search Vehicles
          </CustomPrimaryButton>
        </CancellationSuccess>
      )}
      {/* Pre-order Cancellation Failure */}
      {showCancellationFailure && (
        <CancellationFailure data-testid="cancel-preorder-failure-text">Please try again later.</CancellationFailure>
      )}
    </PreOrderModal>
  );
};

export default CancelPreOrderModal;
